import { User, useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getApiUrl } from 'src/shared/lib/getApiUrl';
import { AuthLoading } from 'src/features/auth/contexts/AuthLoading';
import { cleanseUrl } from 'src/features/auth/contexts/nextValidator';
import { useRum } from 'src/features/auth/contexts/useRum';
import { LinkAccountProvider, useLinkAccountContext } from 'src/features/auth/contexts/LinkAccountContext';
import getRedirectUrl from 'src/features/auth/contexts/redirectUrl';
import { RedirectException } from 'src/shared/queries/errors/RedirectException';
import { useMe } from 'src/shared/queries/useMe';
import mixpanel from 'src/shared/lib/mixpanel';
import { Me } from 'src/shared/types/user.types';

const TIMEOUT_DELAY = 300;

interface FetchResponse<T> extends Response {
  json: () => Promise<T>;
}

// Create a stable apiFetch function outside the component
function createApiFetch(
  getAccessTokenSilently: () => Promise<string>,
  navigateFn: (path: string) => void,
  redirectPrefix?: string
) {
  return async function apiFetch<T = unknown>(
    path: string,
    init: RequestInit = {},
    skipDefaultHeaders = false
  ): Promise<FetchResponse<T>> {
    const token = await getAccessTokenSilently();

    if (!skipDefaultHeaders) {
      init.headers = {
        'Content-Type': 'application/json',
        ...init.headers,
        Authorization: `Bearer ${token}`,
      };
    } else {
      init.headers = {
        Authorization: `Bearer ${token}`,
        ...init.headers,
      };
    }

    if (!import.meta.env.VITE_API_BASE_URL) {
      throw new Error('No base API URL specified');
    }

    const url = new URL(getApiUrl() + path);

    const res = await fetch(`${url.toString()}`, {
      method: 'GET',
      ...init,
    });

    // handle redirect
    if (res.status === 307) {
      const tempRes = res.clone();
      const data = await tempRes.json();
      if ('redirect' in data) {
        navigateFn(`${redirectPrefix ?? ''}${data?.redirect}`);
      }
    }

    return res;
  };
}

interface UseAuthProps {
  user: (User & Me) | undefined;
  isLoading: boolean;
  logout: () => void;
  apiFetch: <T>(
    path: string,
    init?: RequestInit | undefined,
    skipDefaultHeaders?: boolean
  ) => Promise<FetchResponse<T>>;
}

const AuthContext = createContext<UseAuthProps>({
  user: undefined,
  isLoading: true,
  logout: () => {},
  apiFetch: () => new Promise(() => {}),
});

export function useAuth() {
  return useContext(AuthContext);
}

let returnTo: string | undefined = undefined;

interface AuthProviderProps {
  children: ReactNode;
  redirectPrefix?: string;
  logoutOverride?: () => void;
}

export function AuthProvider(props: AuthProviderProps) {
  const { redirectPrefix } = props;

  return (
    <LinkAccountProvider redirectPrefix={redirectPrefix}>
      <AuthProviderInner {...props} />
    </LinkAccountProvider>
  );
}

function AuthProviderInner({ children, redirectPrefix, logoutOverride }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const { needsLinking, isChecking } = useLinkAccountContext();
  const authMountTime = useRef(Date.now());

  const authLog = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.info(message, {
      ...context,
      source: 'AuthContext',
      currentPath,
      redirectPrefix,
      elapsedMs: Date.now() - authMountTime.current,
    });
  };

  const authLogError = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.error(message, {
      ...context,
      source: 'AuthContext',
      currentPath,
      redirectPrefix,
      elapsedMs: Date.now() - authMountTime.current,
    });
  };

  authLog('[mount] AuthProviderInner mounting', { currentPath, redirectPrefix });

  const {
    data: user,
    isError,
    error,
  } = useMe(
    redirectPrefix,
    currentPath.includes('/link-account') ||
      currentPath.includes('/verify-email') ||
      currentPath.includes('/enable-mfa')
  );

  const { logout: auth0Logout, user: auth0User, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [timeoutLoading, setTimeoutLoading] = useState(true);

  returnTo = search.next ? search.next : returnTo;
  useRum(auth0User);

  const isRedirecting = useMemo(() => error instanceof RedirectException, [error]);

  const logoutCb = useCallback(async () => {
    if (logoutOverride) {
      return logoutOverride();
    }
    try {
      mixpanel.reset();
      localStorage.clear();
      if (isAuthenticated) {
        const hostname = window.location.hostname + (window.location.port ? `:${window.location.port}` : '');
        const determinedReturnTo = `${getRedirectUrl(hostname)}${redirectPrefix ?? ''}/login`;

        await auth0Logout({
          logoutParams: {
            returnTo: determinedReturnTo,
          },
        });
      } else {
        navigate({ to: `${redirectPrefix ?? ''}/login` });
      }
    } catch (err) {
      console.error('Logout failed:' + err);
    }
  }, [logoutOverride, isAuthenticated, redirectPrefix, auth0Logout, navigate]);

  useEffect(() => {
    authLog('[auth0State] auth0 state changed', {
      isLoading,
      isAuthenticated,
      hasAuth0User: !!auth0User,
      auth0UserEmail: auth0User?.email ?? 'undefined',
      hasUser: !!user,
      isError,
      errorType: error ? error.constructor.name : 'none',
      errorMessage: error ? String(error) : 'none',
      needsLinking,
      isChecking,
      isRedirecting,
    });
  }, [isLoading, isAuthenticated, auth0User, user, isError, error, needsLinking, isChecking, isRedirecting]);

  useEffect(() => {
    if (needsLinking || isChecking) {
      authLog('[linkCheck] skipping error check', { needsLinking, isChecking });
      return;
    }
    if (isError && !(error instanceof RedirectException)) {
      authLogError('[authError] useMe error, triggering logout', { errorMessage: String(error) });
      (logoutOverride || logoutCb)();
      return;
    }
  }, [error, isError, logoutCb, logoutOverride, needsLinking, isChecking]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      authLog('[notAuthenticated] redirecting to login', {
        redirectTo: `${redirectPrefix ?? ''}/login?next=${currentPath}`,
      });
      navigate({ to: `${redirectPrefix ?? ''}/login`, search: { next: currentPath } });
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      authLog('[timeout] loading timeout elapsed');
      setTimeoutLoading(false);
    }, TIMEOUT_DELAY);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (
      user &&
      auth0User &&
      returnTo &&
      !currentPath.includes('/link-account') &&
      !currentPath.includes('/no-access') &&
      !currentPath.includes('/enable-mfa')
    ) {
      navigate({ to: `${redirectPrefix ?? ''}` + cleanseUrl(returnTo) });
      returnTo = undefined;
    }
  }, [auth0User, user]);

  const navigateTo = useCallback((path: string) => navigate({ to: path }), [navigate]);

  const apiFetch = useMemo(
    () => createApiFetch(getAccessTokenSilently, navigateTo, redirectPrefix ?? undefined),
    [getAccessTokenSilently, navigateTo, redirectPrefix]
  );

  const mergedUser = useMemo(() => {
    return { ...(auth0User as object), ...(user as object) } as unknown as (User & Me) | undefined;
  }, [auth0User, user]);

  const authIsLoading = isLoading || !isAuthenticated || !user || !auth0User || isRedirecting;

  const value: UseAuthProps = useMemo(
    () => ({
      user: mergedUser,
      isLoading: authIsLoading,
      logout: logoutCb,
      apiFetch,
    }),
    [mergedUser, authIsLoading, logoutCb, apiFetch]
  );

  const shouldShowLoading = isLoading || !isAuthenticated || !user || !auth0User || timeoutLoading || isRedirecting;
  const shouldBypassLoading =
    currentPath.includes('/link-account') ||
    currentPath.includes('/verify-email') ||
    currentPath.includes('/no-access') ||
    currentPath.includes('/enable-mfa');

  authLog('[render] AuthProvider rendering', {
    shouldShowLoading,
    shouldBypassLoading,
    breakdown: {
      isLoading,
      isAuthenticated: !isAuthenticated,
      noUser: !user,
      noAuth0User: !auth0User,
      timeoutLoading,
      isRedirecting,
    },
    isError,
  });

  return (
    <AuthContext.Provider value={value}>
      {shouldShowLoading && !shouldBypassLoading ? (
        <div data-testid="auth-loading">
          <AuthLoading
            text={isError ? 'An unknown error occurred. Please try again later' : 'Logging in'}
            delay={TIMEOUT_DELAY}
            from={0}
            to={70}
          />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
